import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import AppBar from "@material-ui/core/AppBar";
import CssBaseline from "@material-ui/core/CssBaseline";
import Drawer from "@material-ui/core/Drawer";
import Hidden from "@material-ui/core/Hidden";
import IconButton from "@material-ui/core/IconButton";
import MenuIcon from "@material-ui/icons/Menu";
import Toolbar from "@material-ui/core/Toolbar";

import {
  makeStyles,
  useTheme,
  Theme,
  createStyles,
} from "@material-ui/core/styles";

import Header from "./Header";
import Sidebar from "./Sidebar/Sidebar";
import IVenueSettings from "../interfaces/IVenueSettings";
import PromptManager from "./PromptManager";

interface IPageLayoutProps {
  /** Sets if the navbar header should be shown */
  showHeader?: boolean;
  /** Whether or not to display the sidebar */
  sidebar?: boolean;
  /** Web page tab title */
  title: string;
  venueSettingsData?: IVenueSettings;
  isLoading?: boolean;
  CustomSidebar?: FunctionComponent;
  CustomHeader?: FunctionComponent;
  sideWidth?: string;
  customSidebarProps?: {[key: string]: any;}
}
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
      minHeight: "100%",
    },
    drawer: {
      [theme.breakpoints.up("md")]: {
        width: ({ drawerWidth }: any) => drawerWidth,
        flexShrink: 0,
      },
    },
    appBar: {
      [theme.breakpoints.up("md")]: {
        marginLeft: ({ drawerWidth }: any) => drawerWidth,
      },
      backgroundColor: "transparent",
      boxShadow: "0px 2px 5px 0px rgba(0, 0, 0, 0.2)",
    },
    menuButton: {
      marginRight: theme.spacing(2),
      [theme.breakpoints.up("md")]: {
        display: "none",
      },
    },
    // necessary for content to be below app bar
    toolbar: theme.mixins.toolbar,
    drawerPaper: {
      width: ({ drawerWidth }: any) => drawerWidth as any,
      overflowX: "hidden",
    },
    content: {
      flexGrow: 1,
      position: "relative",
      minHeight: "100%",
      padding: theme.spacing(3),
    },

    LayoutContainer: {
      "& > header": {
        borderBottom: "unset !important",
        "& > div": {
          backgroundColor: "#f3f4f9 !important",
          borderBottom: "2px solid #e8eaf4",
        },
      },
    },
  })
);
const PageLayout: FunctionComponent<IPageLayoutProps> = (props) => {
  const {
    children,
    showHeader,
    sidebar,
    title,
    venueSettingsData,
    isLoading = false,
    CustomSidebar,
    CustomHeader,
    sideWidth,
    customSidebarProps
  } = props;

  const drawerWidth = sideWidth ? sideWidth : sidebar ? 300 : 0;
  const classes = useStyles({ drawerWidth });
  const theme = useTheme();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const { t } = useTranslation();

  const renderHeader = () => {
    if (showHeader) {
      return CustomHeader ? <CustomHeader /> : <Header title={title} isLoading={isLoading} />;
    }

    return null;
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <div style={{ height: "100%" }}>
      {CustomSidebar ? (
        <CustomSidebar  {...customSidebarProps}/>
      ) : (
        <Sidebar
          venueSettingsData={venueSettingsData}
          setMobileOpen={setMobileOpen}
          isLoading={isLoading}
        />
      )}
    </div>
  );

  return (
    <>
      <title>{t(title)}</title>

      <div
        data-component-name={!isLoading && "Layout"}
        className={`${classes.root} ${classes.LayoutContainer}`}
      >
        <CssBaseline />

        <AppBar position="fixed" className={classes.appBar}>
          <Toolbar>
            {sidebar ? (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                className={classes.menuButton}
              >
                <MenuIcon />
              </IconButton>
            ) : (
              ""
            )}

            {renderHeader()}
          </Toolbar>
        </AppBar>

        {sidebar ? (
          <nav className={classes.drawer} aria-label="">
            {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
            <Hidden mdUp implementation="css">
              <Drawer
                data-component-name="Drawer-Mobile"
                variant="temporary"
                anchor={theme.direction === "rtl" ? "right" : "left"}
                open={mobileOpen}
                onClose={handleDrawerToggle}
                classes={{
                  paper: classes.drawerPaper,
                }}
                ModalProps={{
                  keepMounted: true, // Better open performance on mobile.
                }}
              >
                {drawer}
              </Drawer>
            </Hidden>

            <Hidden smDown implementation="css">
              <Drawer
                data-component-name="Drawer-Desktop"
                classes={{
                  paper: classes.drawerPaper,
                }}
                variant="permanent"
                open
              >
                {drawer}
              </Drawer>
            </Hidden>
          </nav>
        ) : (
          ""
        )}

        <main className={classes.content}>
          <div className={classes.toolbar} />

          {children}
        </main>
      </div>

      {!isLoading && <PromptManager />}
    </>
  );
};

export default PageLayout;
